export interface PvPRoom {
  roomId: string
  creatorAddress: string
  creatorNickname?: string
  creatorLevel?: number
  creatorExp?: number
  creatorEcs?: number
  creatorUsdt?: number
  creatorAvatar?: string | null
  creatorCharacters?: any[] // Array of 1-3 characters
  creatorCharacter?: any // Legacy single character (deprecated)
  joinerAddress?: string
  joinerNickname?: string
  joinerLevel?: number
  joinerExp?: number
  joinerEcs?: number
  joinerUsdt?: number
  joinerAvatar?: string | null
  joinerCharacters?: any[] // Array of 1-3 characters
  joinerCharacter?: any // Legacy single character (deprecated)
  entryFee: number
  roomType?: 'free' | 'usdt' | 'ecs'
  characterCount?: number // Number of characters per player (1, 2, or 3), default 3
  canJoin?: boolean
  status: 'waiting' | 'character_selection' | 'ready' | 'payment_pending' | 'payment_completed' | 'in_battle' | 'battle_started' | 'cancelled' | 'completed'
  isCreator?: boolean
  isJoiner?: boolean
  createdAt?: string
  battleId?: string
}

export interface PvpBattleFinishedCharacterSummary {
  characterInstanceId: string
  characterId: string
  characterName: string
  rarity: string
}

/** Top earner in last 24h (rolling) for lobby “Biggest PvP Payouts”: sum of net winner bonus in that window. */
export interface PvpTodayRankedChampion {
  wallet: string
  nickname: string | null
  battleWins: number
  totalBonus: number
}

export interface PvpTodayRankedChampions {
  usdt: PvpTodayRankedChampion | null
  ecs: PvpTodayRankedChampion | null
}

/** Global lobby live feed row (ranked ECS/USDT only); from HTTP hydrate + `server-time-tick` `battleFeedList`. */
export interface PvpRankedBattleFeedEntry {
  cursorId?: string
  battleId: string
  roomId: string
  battleEndedAt: string
  player1: string
  player2: string
  /** Set when hydrated from API/socket (including bot display level) */
  player1Level?: number | null
  player2Level?: number | null
  player1Nickname: string | null
  player2Nickname: string | null
  winnerWallet: string | null
  isTie: boolean
  currency: 'ecs' | 'usdt'
  /** Entry stake (room ECS price or USDT price) for “in N ECS/USDT room”. */
  stakeAmount: number
  winnerAmount: number
  loserAmount: number
  winnerBonusEcs?: number
}

/** Compact character row for active ranked lobby list (from battle entities). */
export interface PvpActiveRankedBattleCharacterThumb {
  characterId: string
  imageUrl: string | null
  /** For static portrait art (`{id}-{rarity}.webp`). */
  rarity?: string | null
}

/** In-progress ECS/USDT stake battle (ranked matchmaking or ranked direct challenge). */
export interface PvpActiveRankedBattleEntry {
  battleId: string
  roomId: string
  player1: string
  player2: string
  player1Nickname: string | null
  player2Nickname: string | null
  player1Level?: number | null
  player2Level?: number | null
  player1AvatarUrl?: string | null
  player2AvatarUrl?: string | null
  player1Characters?: PvpActiveRankedBattleCharacterThumb[]
  player2Characters?: PvpActiveRankedBattleCharacterThumb[]
  currency: 'ecs' | 'usdt'
  /** Room stake (`price`), same units as lobby USDT/ECS stakes. */
  stakeAmount: number
  startedAt: string
}

export interface PvpMmrLeaderboardEntry {
  rank: number
  walletAddress: string
  nickName: string | null
  userLvl: number
  userExp: number
  pvpMmr: number
  pvpWins: number
  pvpLosses: number
  pvpTies: number
  avatarUrl: string | null
  /** Row from ranked PvP logs (training opponent), not a User account */
  isTrainingBot?: boolean
  /** Direct challenge opponent list: socket presence when returned by search-players API */
  isOnline?: boolean
  /** Tournament hub: show tournament points in the trailing column instead of `pvpMmr`. Honor badge still uses `pvpMmr`. */
  tournamentScore?: number
  /** Public tournament leaderboard only: earned this tournament (participant rank). */
  usdtEarned?: number
  ecsEarned?: number
  /** Optional: from authenticated `/user/tournament/season-result` (default placement ladder). */
  expectedPlacementEcs?: number
  expectedPlacementUsdt?: number
  /** Friend list/profile only: kingdom power (`User.userScore`). */
  userScore?: number
  /** Friend list/profile only: their alliance badge, or null when they're not in one. */
  allianceName?: string | null
  allianceFlagUrl?: string | null
}

/** Gap row between top 50 and a pinned viewer when their global rank is below 50. */
export type PvpMmrLeaderboardEllipsisRow = { rowType: 'ellipsis' }

export type PvpMmrLeaderboardListRow = PvpMmrLeaderboardEntry | PvpMmrLeaderboardEllipsisRow

export function isPvpMmrLeaderboardEllipsisRow(row: PvpMmrLeaderboardListRow): row is PvpMmrLeaderboardEllipsisRow {
  return 'rowType' in row && row.rowType === 'ellipsis'
}

/** One row from PvPBattleFinishedLog (compact post-battle record). */
export interface PvpBattleFinishedLogEntry {
  id: string
  battleId: string
  roomId: string
  player1: string
  player1Characters: PvpBattleFinishedCharacterSummary[]
  player2: string
  player2Characters: PvpBattleFinishedCharacterSummary[]
  /** From User.userLvl at list time (wallet join); null if user missing */
  player1Level: number | null
  player2Level: number | null
  /** From User.nickName at list time; null → UI falls back to shortened wallet */
  player1Nickname: string | null
  player2Nickname: string | null
  /** From User.pvpMmr at list time; null if user missing */
  player1PvpMmr: number | null
  player2PvpMmr: number | null
  /** From User.currentAvatar.imageUrl at list time; null if missing */
  player1AvatarUrl: string | null
  player2AvatarUrl: string | null
  stakeType: 'free' | 'usdt' | 'ecs'
  stakeAmount: number
  winnerWallet: string | null
  battleEndedAt: string
  endReason: string
}

/** Tournament hub activity: a player joined via the join tournament flow (server: `tournament_participants`). */
export interface TournamentActivityJoinEntry {
  kind: 'join'
  participantId: string
  walletAddress: string
  nickname: string | null
  userLevel: number | null
  pvpMmr: number | null
  avatarUrl: string | null
  joinedAt: string
}

/** Finished battle row in tournament activity feed (`kind` discriminant). */
export type TournamentActivityBattleEntry = PvpBattleFinishedLogEntry & { kind: 'battle' }

export type TournamentHubActivityItem = TournamentActivityBattleEntry | TournamentActivityJoinEntry

export interface PendingBattleOptions {
  characterCount: number
  roomType: 'free' | 'usdt' | 'ecs'
  amount: number
  mode: 'unranked' | 'ranked'
  /** Set when user entered PvP lobby from the tournament hub (matchmaking + battle logs). */
  isTournament?: boolean
}

export interface PvpIncomingRematchRequest {
  previousBattleId: string
  requesterWallet: string
  requesterNickname: string
  /** Paid rematch stake (from room `price`); 0 or omitted for free. */
  rematchStakeAmount?: number
  rematchRoomType?: 'free' | 'usdt' | 'ecs' | string
}

export interface PvpAcceptedRematch {
  previousBattleId: string
  roomId: string
  battleId: string
  countdownSeconds: number
  winnerWallet: string
  loserWallet: string
  winnerNickname: string
  loserNickname: string
  stakeLabel: string
}

export type PvpFriendRelationStatus = 'none' | 'friend' | 'pending_out' | 'pending_in'

/** Shown to the requester when the recipient declines (global alert). */
export interface PvpFriendRequestDeclinedNotice {
  requestId?: string
  recipientWallet: string
  recipientNickname: string
}

/** Incoming PvP friend request (recipient socket / hydrate). */
export interface PvpIncomingFriendRequest {
  requestId: string
  requesterWallet: string
  requesterNickname: string
  requesterAvatarUrl?: string | null
  requesterUserLvl?: number
  requesterPvpMmr?: number
  createdAtUnixMs?: number
}

/** Incoming direct challenge (opponent socket). */
export interface PvpIncomingDirectChallengeRequest {
  challengeId: string
  requesterWallet: string
  requesterNickname: string
  /** Current profile avatar image URL when present (socket payload). */
  requesterAvatarUrl?: string | null
  /** Account level (1+). */
  requesterUserLvl?: number
  /** PvP matchmaking rating from User. */
  requesterPvpMmr?: number
  battleMode: 'ranked' | 'unranked'
  stakeCurrency?: 'usdt' | 'ecs'
  stakeAmount: number
  characterCount: number
  /** Server Unix ms when pending challenge TTL ends (socket may omit on older servers). */
  pendingExpiresAtUnixMs?: number
  challengeCreatedAtUnixMs?: number
}

/** After accept: joiner session (opponent) locks settings on direct challenge page. */
export interface DirectChallengeJoinerLock {
  challengeId: string
  requesterWallet: string
  requesterNickname: string
  /** From incoming challenge at accept time (HTTP); used on joiner accepted screen. */
  requesterAvatarUrl?: string | null
  requesterUserLvl?: number
  requesterPvpMmr?: number
  battleMode: 'ranked' | 'unranked'
  stakeCurrency?: 'usdt' | 'ecs' | null
  stakeAmount: number
  characterCount: number
}

/** Requester is waiting for the joiner to finish roster prep (after socket accept, before join-battle). */
export interface DirectChallengeAwaitingJoinInfo {
  challengeId: string
  /** Challenger wallet; only that client should render the waiting modal. */
  requesterWallet: string
  opponentNickname: string
  opponentWallet: string
  opponentAvatarUrl?: string | null
  opponentUserLvl?: number
  opponentPvpMmr?: number
  battleMode: 'ranked' | 'unranked'
  stakeCurrency?: 'usdt' | 'ecs' | null
  stakeAmount: number
  characterCount: number
  /** Server Unix ms when join-prep window ends (authoritative). */
  joinPrepExpiresAtUnixMs: number
  /**
   * Server Unix ms when the challenge was created (`createdAt`).
   * Remaining UI uses `ttlMs - (serverNow - challengeCreatedAtUnixMs)` with `ttlMs = joinPrepExpiresAtUnixMs - challengeCreatedAtUnixMs`
   * (equivalent to `joinPrepExpiresAtUnixMs - serverNow` when both are server-aligned).
   */
  challengeCreatedAtUnixMs?: number
  /**
   * True when the challenger is waiting for accept/decline (HTTP request sent; challenge still pending).
   * Cleared when `pvp:direct-challenge-accepted` replaces this slice with join-prep data.
   */
  awaitingAcceptDecline?: boolean
  /**
   * True once `pvp:direct-challenge-countdown` fires — the battle is counting down.
   * The modal stays visible in this state instead of disappearing.
   */
  countingDown?: boolean
}

export interface PvPState {
  rooms: PvPRoom[]
  myRooms: PvPRoom[]
  currentRoom: PvPRoom | null
  loading: boolean
  error: string | null
  refreshing: boolean
  /** Last fetch from GET/POST battle-finished-logs (PvP lobby). */
  battleFinishedLogs: PvpBattleFinishedLogEntry[]
  battleFinishedLogsTotal: number
  battleFinishedLogsLoading: boolean
  /** Global ranked usdt/ECS finishes for bottom-left live feed. */
  liveRankedBattleFeed: PvpRankedBattleFeedEntry[]
  liveRankedBattleFeedLoading: boolean
  /** In-progress ranked stake battles (matchmaking + ranked direct challenge); hydrated on hub + socket when used. */
  activeRankedBattles: PvpActiveRankedBattleEntry[]
  activeRankedBattlesLoading: boolean
  todayRankedBattleChampions: PvpTodayRankedChampions | null
  todayRankedBattleChampionsLoading: boolean
  pvpMmrLeaderboardWalletAddress: string | null
  pvpMmrLeaderboardEntries: PvpMmrLeaderboardListRow[]
  /** Ranked training bots from PVPLog (first leaderboard page only). */
  pvpMmrLeaderboardBotShowcase: PvpMmrLeaderboardEntry[]
  pvpMmrLeaderboardTotal: number
  /** When true, MMR leaderboard response is a fixed top-50 snapshot (no infinite scroll). */
  pvpMmrLeaderboardSnapshotMode: boolean
  pvpMmrLeaderboardViewerEntry: PvpMmrLeaderboardEntry | null
  /** True while user is in /pvp after clicking Enter Battle from tournament hub (until leaving lobby). */
  isTournamentLobby: boolean
  /** When set, `/pvp/play` hides ranked/unranked tab buttons and locks the join panel to this mode (from `/pvp` hub). */
  playLobbyLockedMode: 'ranked' | 'unranked' | null
  pendingBattle: {
    battleOptions: PendingBattleOptions | null
    selectedCharacters: any[]
  }
  rematchRequestPendingBattleId: string | null
  usedRematchBattleIds: string[]
  incomingRematchRequest: PvpIncomingRematchRequest | null
  isRespondingToRematch: boolean
  acceptedRematch: PvpAcceptedRematch | null
  acceptedRematchCountdown: number | null
  declinedRematchMessage: string | null
  incomingDirectChallenge: PvpIncomingDirectChallengeRequest | null
  directChallengeRespondBusy: boolean
  directChallengeAwaitingJoin: DirectChallengeAwaitingJoinInfo | null
  directChallengeDeclinedMessage: string | null
  directChallengeJoinerLock: DirectChallengeJoinerLock | null
  /** When set, `pvp:battle-started` for this room navigates from the direct-challenge flow. */
  directChallengeActiveRoomId: string | null
  /** After join-battle countdown: wait for room creation + `pvp:battle-started` (survives ref resets). */
  directChallengeBattleStartPending: boolean
  incomingFriendRequest: PvpIncomingFriendRequest | null
  friendRequestRespondBusy: boolean
  friendRequestDeclinedNotice: PvpFriendRequestDeclinedNotice | null
}

const initialState: PvPState = {
  rooms: [],
  myRooms: [],
  currentRoom: null,
  loading: false,
  error: null,
  refreshing: false,
  battleFinishedLogs: [],
  battleFinishedLogsTotal: 0,
  battleFinishedLogsLoading: false,
  liveRankedBattleFeed: [],
  liveRankedBattleFeedLoading: true,
  activeRankedBattles: [],
  activeRankedBattlesLoading: true,
  todayRankedBattleChampions: null,
  todayRankedBattleChampionsLoading: true,
  pvpMmrLeaderboardWalletAddress: null,
  pvpMmrLeaderboardEntries: [],
  pvpMmrLeaderboardBotShowcase: [],
  pvpMmrLeaderboardTotal: 0,
  pvpMmrLeaderboardSnapshotMode: false,
  pvpMmrLeaderboardViewerEntry: null,
  isTournamentLobby: false,
  playLobbyLockedMode: null,
  pendingBattle: {
    battleOptions: null,
    selectedCharacters: [],
  },
  rematchRequestPendingBattleId: null,
  usedRematchBattleIds: [],
  incomingRematchRequest: null,
  isRespondingToRematch: false,
  acceptedRematch: null,
  acceptedRematchCountdown: null,
  declinedRematchMessage: null,
  incomingDirectChallenge: null,
  directChallengeRespondBusy: false,
  directChallengeAwaitingJoin: null,
  directChallengeDeclinedMessage: null,
  directChallengeJoinerLock: null,
  directChallengeActiveRoomId: null,
  directChallengeBattleStartPending: false,
  incomingFriendRequest: null,
  friendRequestRespondBusy: false,
  friendRequestDeclinedNotice: null,
}

export default initialState

